'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

import type { HeaderData, MenuCard, MenuGroup } from './header-data';

import Link from '@eliadmin/core/src/components/link';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { CmsIcon, CmsImage } from '@eliadmin/core/src/components/svetech/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { Logo } from '@eliadmin/core/src/components/svetech/ui/logo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { HeaderActions } from './header-actions';

const ICON_BUTTON =
  'flex size-6 rounded-sm text-[#14171a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-2';

/** The group of the current page, else the first one (Figma draws the menu with a group open). */
function initialGroup(groups: MenuGroup[], pathname: string) {
  const current = groups.find(
    group => group.href === pathname || group.cards.some(card => card.href === pathname),
  );
  return String((current ?? groups[0])?.id ?? '');
}

/**
 * Mobile menu below `lg` — Figma 4711:4059 / 4711:4228 / 4711:4387: full-width panel under the
 * same 64 px bar (close icon instead of the menu icon). Navigation (pt 12, px 16, pb 24, 24 px
 * apart): accordion of the category groups with their cards and the „show all“ link, the promo
 * card of the open group with the trust badges, the header links and the advice link.
 */
export function MobileMenu({
  links,
  groups,
  benefits,
  phone,
  quote,
  advice,
  quoteShortLabel,
}: HeaderData & { quoteShortLabel: string }) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const [openGroupId, setOpenGroupId] = useState(() => initialGroup(groups, pathname));
  const { t } = useTranslations();

  useEffect(() => setOpen(false), [pathname]);

  const onOpenChange = (next: boolean) => {
    if (next) setOpenGroupId(initialGroup(groups, pathname));
    setOpen(next);
  };

  const openGroup = groups.find(group => String(group.id) === openGroupId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger aria-label={t('text3241')} className={ICON_BUTTON}>
        {/* Figma „Icon / List“: three 16 px lines, 5 px apart, stroke 1.75. */}
        <svg viewBox="0 0 24 24" aria-hidden="true" className="size-6">
          <path
            d="M4 7H20M4 12H20M4 17H20"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
          />
        </svg>
      </SheetTrigger>
      <SheetContent
        side="left"
        showCloseIcon={false}
        aria-describedby={undefined}
        // Links to the current page (or an anchor) do not change the pathname.
        onClick={event => {
          if ((event.target as HTMLElement).closest('a')) setOpen(false);
        }}
        className="flex w-full flex-col gap-0 overflow-y-auto border-r-0 bg-white p-0 sm:max-w-[420px]"
      >
        <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>
        <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-[#e6e8e2] px-4">
          <div className="flex shrink-0 items-center gap-4">
            <SheetClose aria-label={t('text272')} className={ICON_BUTTON}>
              <Icon name="close" className="size-6" />
            </SheetClose>
            <Link href="/" prefetch={false} aria-label="SveTech — domov">
              <Logo className="w-[91px] text-[#2c7a25]" />
            </Link>
          </div>
          <HeaderActions phone={phone} quote={quote} quoteShortLabel={quoteShortLabel} />
        </div>
        <nav aria-label={t('text3241')} className="flex flex-col gap-6 px-4 pb-6 pt-3">
          <div className="flex flex-col gap-6">
            <Accordion
              type="single"
              collapsible
              value={openGroupId}
              onValueChange={setOpenGroupId}
              className="flex flex-col"
            >
              {groups.map(group => (
                <AccordionItem
                  key={group.id}
                  value={String(group.id)}
                  className="border-b border-t-0 border-[#e6e8e2]"
                >
                  <AccordionTrigger
                    hideIcon
                    className="svt-button group gap-3 py-4 font-bold leading-6 text-[#14171a] hover:no-underline data-[state=open]:text-[#2c7a25]"
                  >
                    <CmsIcon image={group.icon} className="size-6" />
                    <span className="min-w-0 flex-1 text-left">{group.name}</span>
                    <Icon
                      name="caret-down"
                      className="size-4 text-[#6b7681] transition-transform group-data-[state=open]:rotate-180"
                    />
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col pb-4">
                    {group.cards.map(card => (
                      <MenuItem key={card.id} card={card} />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            {openGroup?.href && openGroup.linkLabel ? (
              <Link
                href={openGroup.href}
                prefetch={false}
                className="svt-button flex items-center gap-2 self-center text-[#2c7a25]"
              >
                {openGroup.linkLabel}
                <Icon name="arrow-right" className="size-5" />
              </Link>
            ) : null}
          </div>
          {openGroup?.promo.title || benefits.length ? (
            <div className="flex flex-col gap-4">
              {openGroup ? <PromoCard promo={openGroup.promo} /> : null}
              {/* Figma „Trust“: wrapped badges, 16 / 12 px apart, without the dots of the hero. */}
              {benefits.length ? (
                <ul className="flex flex-wrap items-center gap-x-4 gap-y-3">
                  {benefits.map(item => (
                    <li key={item.id} className="flex items-center gap-2">
                      <CmsIcon image={item.icon} className="size-5" />
                      <span className="svt-benefit text-[#14171a]">{item.name}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <ul className="flex flex-col gap-3">
            {links.map(link => (
              <li key={link.id}>
                {/* Figma rows are 24 px tall (36 px apart). */}
                <Link
                  href={link.href}
                  prefetch={false}
                  className="svt-button block font-medium leading-6 text-[#14171a] hover:text-[#2c7a25]"
                >
                  {link.label}
                </Link>
              </li>
            ))}
            {advice ? (
              <li>
                <Link
                  href={advice.href}
                  prefetch={false}
                  className="svt-button flex items-center gap-2 font-bold leading-6 text-[#2c7a25] hover:text-[#123410]"
                >
                  <Icon name="question" className="size-6" />
                  {advice.label}
                </Link>
              </li>
            ) : null}
          </ul>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

/** Figma „Menu item“: 72 px row, padding 8, 56 px thumbnail (12/0/12/12), title 18 + Body S. */
function MenuItem({ card }: { card: MenuCard }) {
  const content = (
    <>
      <span className="relative block size-14 shrink-0 overflow-hidden rounded-xl rounded-tr-none bg-[#f2f3ef]">
        <CmsImage image={card.image} decorative sizes="56px" />
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="svt-title-menu text-[#14171a]">{card.title}</span>
        {card.description ? (
          <span className="svt-body-s text-[#4a5560]">{card.description}</span>
        ) : null}
      </span>
    </>
  );
  const className = 'flex items-center gap-3 rounded-[15px] rounded-tr-none p-2';

  return card.href ? (
    <Link
      href={card.href}
      prefetch={false}
      className={cn(
        className,
        'transition-colors hover:bg-[#f2f3ef] focus-visible:bg-[#f2f3ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25]',
      )}
    >
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}

/**
 * Figma „Promo card“ (4699:4706): 260 px photo card, 24/0/24/24, padding 16, gradient to black
 * over the lower 250 px, 44 px yellow arrow tile, H3 white + Body #bcc0c3. The gradient's
 * background blur does not reach the card's own image fill in Figma (the photo stays sharp).
 */
function PromoCard({ promo }: { promo: MenuGroup['promo'] }) {
  if (!promo.title) return null;

  const content = (
    <>
      <CmsImage image={promo.image} decorative sizes="(min-width: 640px) 388px, 100vw" />
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[250px] bg-gradient-to-b from-transparent to-black"
      />
      <span className="relative flex size-11 items-center justify-center rounded-xl rounded-tr-none bg-[#f1c40f] text-[#14171a]">
        <Icon name="arrow-up-right" className="size-5" />
      </span>
      <div className="relative flex w-full flex-col gap-2">
        <Heading tag="h3" as="p" tone="dark" title={promo.title} />
        {promo.text ? <span className="svt-body text-[#bcc0c3]">{promo.text}</span> : null}
      </div>
    </>
  );
  const className =
    'relative flex h-[260px] flex-col items-end justify-between overflow-hidden rounded-3xl rounded-tr-none bg-[#14171a] p-4';

  return promo.href ? (
    <Link
      href={promo.href}
      prefetch={false}
      className={cn(
        className,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-2',
      )}
    >
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}
